import shuffle from 'lodash/shuffle';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import Cookies from 'js-cookie';
import {
    CompanyTypeKeyEnum,
    isEventPlannerOrVenue
} from '~/utils/company.utils';
import {
    BUY_NOW_EXPERIENCE_QUERY_PARAM,
    GTM_CAMPAIGN_URL_COOKIE_KEY,
    SIGNUP_DATA_STORAGE_KEY
} from '~/components/Signup/constants/general';
import { trackError } from '~/services/logger/error.utils';
import { Statsig } from 'statsig-react';
import {
    BuyNowData,
    IMarketingSignupData,
    ISignupUserData
} from '~/components/Signup/types/signup.types';
import { getQueryParam } from '~/utils/url.utils';
import { HB_ANONYMOUS_ID_COOKIE_NAME } from '~/common/hooks/useInitHBAnonymousId';
import { getCookie } from '~/utils/dom.utils';
import { LoggerService } from '~/services/logger/logger.service';
import type { TPartner } from '~/apps/main/Partners/partners.types';
import { camelCase } from 'lodash';
import {
    isEmailValid,
    isUniqueEmailDomain,
    getEmailDomain
} from '~/utils/emails.utils';

export const getEmailFocusText = (
    email: string,
    hasEmailError: boolean,
    t: (key: string) => string
): string => {
    const typedEmail = !hasEmailError && email;
    if (typedEmail) {
        const typedEmailSplitted = typedEmail.split('@');
        const isIntegrate = [
            'gmail.com',
            'aol.com',
            'outlook.com',
            'icloud.com',
            'yahoo.com'
        ].includes(typedEmailSplitted[1]);
        if (isIntegrate) {
            return t('email_provider_integrated');
        }
    }
    return '';
};

export const formatCreateUserParams = (data, optimizeTests) => {
    // This is where preparing the params for the
    // server should happen.
    return {
        name: data.name,
        email: data.email,
        auth_token: data.auth_token || '',
        auth_code: data.auth_code || '',
        is_sso: data.is_sso || '', // Server expects "" not `false` 🙄
        is_firebase: data.is_firebase || '',
        sso_type: data.sso_type || 'regular',
        registration_variant: data.registration_variant,
        company_name: data.company_name,
        company_type: data.company_type,
        company_other: data.company_other,
        company_sub_type: data.company_sub_type,
        website_url: data.website_url,
        phone_number: data.phone_number,
        password: data.password,
        is_demo: data.is_demo || 'false',
        injected_ab_test: data.ab_test || '',
        injected_ab_test_variant: data.ab_test_variant || '',
        utm_campaign: data.utmCampaign || data.utm_campaign,
        referral_platform: data.referralPlatform || data.referral_platform,
        utm_content: data.utmContent || data.utm_content,
        utm_medium: data.utmMedium || data.utm_medium,
        utm_source: data.utmSource || data.utm_source,
        utm_customer: data.utmCustomer || data.utm_customer,
        utm_term: data.utmTerm || data.utm_term,
        utm_mbsy: data.utmMbsy || data.code,
        utm_initiative: data.utmInitiative || data.utm_initiative,
        li_fat_id: data.liFatId || data.li_fat_id,
        is_buynow_flow: data.utm_buynowflow || data.buynowflow,
        utm_origin: data.utmOrigin || data.utm_origin,
        optimizely_experiments: data.optimizelyTests,
        optimizely_user_id: data.optimizelyUserId,
        promo: data.promo,
        referral_source: data.referralSource,
        gclid: data.gclid,
        fbclid: data.fbclid,
        irclickid: data.irclickid,
        ttclid: data.ttclid,
        msclkid: data.msclkid,
        placement: data.placement,
        ad_id: data.adId,
        source_template_id: data.sourceTemplateId || data.templateId,
        is_gallery_template: data.isGalleryTemplate || data.is_gallery_template,
        template_redeem_code:
            data.templateRedeemCode || data.template_redeem_code,
        geo_data: data.geo_data || null,
        referrer: data.referrer,
        How_heard_of_us__c: data.How_heard_of_us__c,
        sms_opt_in__c: data.sms_opt_in__c,
        pricing_plan: data.pricing_plan || '',
        signup_landing_page:
            data.signupLandingPage ||
            data.signup_landing_page ||
            logMissingSignupLandingPageFromReferrer(),
        location_data: window.location_data || {}, // This is community registration Google Map form element
        fingerprint: '',
        optimize_experiments: optimizeTests.concat(data.optimize_experiments),
        ab_tests: [...(data.ab_tests || [])],
        is_fullstory_record_enabled: false,
        f_token: '',
        statsig_stable_id: Statsig.getStableID(),
        code: data.code,
        hb_anonymous_id: data.hb_anonymous_id,
        full_url: data.full_url,
        partnership: data.partnership,
        other_utm_params: data.other_utm_params,
        broad_intent_get_started_hero: data.ab_tests?.find(
            test => test.ab_test_name === 'broad_intent_get_started_hero'
        )?.ab_test_variation
    };
};

const logMissingSignupLandingPageFromReferrer = (): string => {
    const referrer = document.referrer;
    if (!referrer) {
        return null;
    }
    try {
        const refUrl = new URL(referrer);
        if (!refUrl.hostname.endsWith('honeybook.com')) {
            return null;
        }

        let signupLandingPage = refUrl.pathname.split('?')[0];
        if (signupLandingPage.startsWith('/')) {
            signupLandingPage = signupLandingPage.substring(1);
        }
        if (signupLandingPage) {
            trackError({
                error: 'signup_landing_page was empty but referer not',
                message: signupLandingPage
            });
        }
        return null;
    } catch (e) {
        trackError({
            error: e,
            message: "Couldn't parse referrer URL for signup_landing_page"
        });
        return null;
    }
};

export type UserData = ISignupUserData &
    IMarketingSignupData &
    Partial<UtmParams> & {
        google_sso_variant: 'regular';
        hb_anonymous_id?: string;
        intake_variant?: string;
    };

export const getIntakeAnalyticsProperties = (data: UserData) => {
    let analyticsProps = {};
    if (data) {
        analyticsProps = {
            website_url: data.website_url || '',
            name: data.name || '',
            email: data.email || '',
            orig_vendor_email: data.vendor_email,
            registration_variant: data.registration_variant || '',
            is_demo: data.is_demo,
            referrer: data.referrer || '',
            signup_landing_page:
                data.signupLandingPage ||
                data.signup_landing_page ||
                logMissingSignupLandingPageFromReferrer(),
            sso_type: data.sso_type || '',
            source: data.source || '',
            intake_variant: data.intake_variant || '',
            sso: data.sso_type || 'none',
            optimize_tests: data.optimize_experiments || '',
            start_trial_variant: data.start_trial_variant,
            company_name: data.company_name || '',
            company_type: data.company_type || '',
            company_other: data.company_other || '',
            company_sub_type: data.company_sub_type || '',
            phone_number: data.phone_number || '',
            how_heard_of_us: data.How_heard_of_us__c || '',
            is_multiple_stage_intake_form: true,
            error_status: '',
            error_type: '',
            error_message: '',
            google_sso_variant: data.google_sso_variant || '',
            utm_source: data.utmSource || '',
            utm_term: data.utmTerm || '',
            utm_campaign: data.utmCampaign || '',
            utm_medium: data.utmMedium || '',
            partnership: data.partnership || '',
            broad_intent_get_started_hero: data.ab_tests?.find(
                test => test.ab_test_name === 'broad_intent_get_started_hero'
            )?.ab_test_variation
        };
    }

    return analyticsProps;
};

export const getHowDidYouHearData = () => {
    const howDidYouHearAboutUs = shuffle([
        { label: 'Referral', value: 'referral' },
        { label: 'Podcast/Radio', value: 'podcast/radio' },
        { label: 'Review Sites', value: 'reviewSites' },
        { label: 'Newsletter', value: 'newsletter' },
        { label: 'TV', value: 'TV' },
        { label: 'Web Search', value: 'searchEngine' },
        { label: 'Facebook/Instagram', value: 'facebookInstagram' },
        { label: 'YouTube', value: 'youTube' },
        { label: 'TikTok', value: 'tiktok' },
        { label: 'LinkedIn', value: 'linkedin' }
    ]);
    howDidYouHearAboutUs.push({ label: 'Other', value: 'other' });
    return howDidYouHearAboutUs;
};

export const getPrefillCompanyType = signupLandingPage => {
    const prefillLandingPages = [
        // Some landing pages allow us to assume the company type
        {
            path: 'photography-contract-template',
            typeKey: CompanyTypeKeyEnum.Photographer
        },
        {
            path: 'event-planner-contract-template',
            typeKey: CompanyTypeKeyEnum.EventPlanner
        },
        {
            path: 'doula-business-software',
            typeKey: CompanyTypeKeyEnum.Doula
        },
        {
            path: 'marketing-consultant-software',
            typeKey: CompanyTypeKeyEnum.Marketing
        },
        {
            path: 'venue-management-software',
            typeKey: CompanyTypeKeyEnum.Venue
        },
        {
            path: 'crm-for-photographers',
            typeKey: CompanyTypeKeyEnum.Photographer
        },
        {
            path: 'crm-for-landscapers',
            typeKey: CompanyTypeKeyEnum.Landscaper
        },
        {
            path: 'dj-booking-software',
            typeKey: CompanyTypeKeyEnum.DjMusician
        },
        {
            path: 'software-for-florists',
            typeKey: CompanyTypeKeyEnum.Florist
        },
        {
            path: 'software-for-event-planners',
            typeKey: CompanyTypeKeyEnum.EventPlanner
        },
        {
            path: 'catering-management-software',
            typeKey: CompanyTypeKeyEnum.Catering
        },
        {
            path: 'crm-for-consultants',
            typeKey: CompanyTypeKeyEnum.BusinessConsultant
        },
        {
            path: 'health-coach-software',
            typeKey: CompanyTypeKeyEnum.Wellness
        },
        {
            path: 'crm-for-graphic-designers',
            typeKey: CompanyTypeKeyEnum.GraphicDesigner
        },
        {
            path: 'free-invoice-templates/doula',
            typeKey: CompanyTypeKeyEnum.Doula
        },
        {
            path: 'free-invoice-templates/graphic-design',
            typeKey: CompanyTypeKeyEnum.GraphicDesigner
        },
        {
            path: 'free-invoice-templates/interior-design',
            typeKey: CompanyTypeKeyEnum.InteriorDesigner
        },
        {
            path: 'free-invoice-templates/coach',
            typeKey: CompanyTypeKeyEnum.Coaching
        },
        {
            path: 'free-invoice-templates/consultants',
            typeKey: CompanyTypeKeyEnum.BusinessConsultant
        },
        {
            path: 'free-invoice-templates/photographers',
            typeKey: CompanyTypeKeyEnum.Photographer
        },
        {
            path: 'free-invoice-templates/tutors',
            typeKey: CompanyTypeKeyEnum.Education
        },
        {
            path: 'free-invoice-templates/cleaners',
            typeKey: CompanyTypeKeyEnum.Cleaning
        },
        {
            path: 'free-invoice-templates/virtual-assistant',
            typeKey: CompanyTypeKeyEnum.VirtualAssistant
        },
        {
            path: 'crm-for-freelancers',
            typeKey: CompanyTypeKeyEnum.Freelancer
        },
        {
            path: 'crm-for-designers',
            typeKey: CompanyTypeKeyEnum.GraphicDesigner
        },
        {
            path: 'contractor-billing-software',
            typeKey: CompanyTypeKeyEnum.Contractor
        },
        {
            path: 'tutor-software',
            typeKey: CompanyTypeKeyEnum.Education
        },
        {
            path: 'virtual-assistant-software',
            typeKey: CompanyTypeKeyEnum.VirtualAssistant
        },
        {
            path: 'real-estate-crm-software',
            typeKey: CompanyTypeKeyEnum.RealEstate
        },
        {
            path: 'social-media-crm',
            typeKey: CompanyTypeKeyEnum.SocialMedia
        },
        {
            path: 'software-for-home-accountants',
            typeKey: CompanyTypeKeyEnum.Accounting
        },
        {
            path: 'interior-design-business-software',
            typeKey: CompanyTypeKeyEnum.InteriorDesigner
        },
        {
            path: 'tools-for-digital-marketers',
            typeKey: CompanyTypeKeyEnum.Marketing
        },
        {
            path: 'software-for-copywriters',
            typeKey: CompanyTypeKeyEnum.Marketing
        },
        {
            path: 'life-coach-software',
            typeKey: CompanyTypeKeyEnum.Coaching
        },
        {
            path: 'software-for-cleaning-business',
            typeKey: CompanyTypeKeyEnum.Cleaning
        },
        {
            path: 'freelance-consulting-contract-template',
            typeKey: CompanyTypeKeyEnum.BusinessConsultant
        },
        {
            path: 'software-for-web-designers',
            typeKey: CompanyTypeKeyEnum.WebDesigner
        },
        {
            path: 'freelance',
            typeKey: CompanyTypeKeyEnum.Freelancer
        },
        {
            path: 'software-for-online-coaching',
            typeKey: CompanyTypeKeyEnum.Coaching
        },
        {
            path: 'software-for-pr-companies',
            typeKey: CompanyTypeKeyEnum.Marketing
        },
        {
            path: 'software-for-agencies',
            typeKey: CompanyTypeKeyEnum.Agency
        },
        {
            path: 'software-for-franchises',
            typeKey: CompanyTypeKeyEnum.Agency
        },
        {
            path: 'band-manager-software',
            typeKey: CompanyTypeKeyEnum.DjMusician
        },
        {
            path: 'software-for-visual-artists',
            typeKey: CompanyTypeKeyEnum.VisualArtist
        },
        {
            path: 'industry-event-planners',
            typeKey: CompanyTypeKeyEnum.EventPlanner
        },
        {
            path: 'industry-photographers',
            typeKey: CompanyTypeKeyEnum.Photographer
        },
        {
            path: 'industry-caterers',
            typeKey: CompanyTypeKeyEnum.Catering
        },
        {
            path: 'industry-venue-managers',
            typeKey: CompanyTypeKeyEnum.Venue
        },
        {
            path: 'industry-social-media',
            typeKey: CompanyTypeKeyEnum.SocialMedia
        },
        {
            path: 'industry-consultants',
            typeKey: CompanyTypeKeyEnum.BusinessConsultant
        },
        {
            path: 'industry-cinematographers',
            typeKey: CompanyTypeKeyEnum.Cinematography
        },
        {
            path: 'industry-marketing',
            typeKey: CompanyTypeKeyEnum.Marketing
        },
        {
            path: 'industry-florists',
            typeKey: CompanyTypeKeyEnum.Florist
        }
    ];

    const landingPageMatch = prefillLandingPages.find(
        page => page.path === signupLandingPage
    );

    return landingPageMatch?.typeKey;
};

export type UtmParams = {
    _fbp: string;
    _fbc: string;
    utmCampaign: string;
    utmContent: string;
    utmMedium: string;
    utmSource: string;
    utmTerm: string;
    utmOrigin: string;
    utmMbsy: string;
    utmInitiative: string;
    liFatId: string;
    gclid: string;
    fbclid: string;
    irclickid: string;
    ttclid: string;
    msclkid: string;
    promo: string;
    referralSource: string;
    referralPlatform: string;
    placement: string;
    adId: string;
    sourceTemplateId: string;
    utm_buynowflow: string;
    templateRedeemCode: string;
    isGalleryTemplate: string;
    code: string;
    full_url: string;
    partnership: TPartner;
    other_utm_params?: Record<string, string>;
};

const getUtmParamsFromCookie = (): Partial<UtmParams> => {
    const utmParamsCookie = Cookies.get('utm_params');
    if (utmParamsCookie) {
        try {
            const utmParamsFromCookie = JSON.parse(utmParamsCookie);
            return utmParamsFromCookie;
        } catch (error) {
            trackError({
                error,
                message: 'Error parsing utm_params cookie'
            });
        }
    }
    return {};
};

export const getUtmParams = (
    formData: Partial<UserData> = {}
): Partial<UtmParams> => {
    let utms: Partial<UtmParams> = {};

    // If the user is not coming from Timeline Genius and is an event planner or venue,
    // we want to offer them Timeline Genius
    let partnership = formData.partnership;
    if (!partnership && isEventPlannerOrVenue(formData.company_type)) {
        partnership = 'timeline_genius_f1';
    }

    try {
        const { URLSearchParams } = window;

        utms._fbp = Cookies.get('_fbp');
        utms._fbc = Cookies.get('_fbc');

        // Add reading from utm_params cookie
        const utmParamsFromCookie = getUtmParamsFromCookie();
        utms = {
            ...utms,
            ...utmParamsFromCookie
        };

        const urlValue = Cookies.get(GTM_CAMPAIGN_URL_COOKIE_KEY);
        if (urlValue) {
            const url = new URL(urlValue);
            const searchParams = new URLSearchParams(url.search);
            const utmSource = searchParams.get('utm_source');

            // Pick this from query string as we might get here from another landing page
            const partnershipFromQuery = searchParams.get('partnership');
            if (partnershipFromQuery) {
                partnership = partnershipFromQuery as TPartner;
            }
            // If utm is already indicating Timeline Genius, avoid of offering Timeline Genius.
            // Go through flows 2-3 (existing members) and not 1 (new members).
            else if (utmSource === 'timeline_genius') {
                partnership = undefined;
            }

            utms = compactObject({
                ...utms,
                utmCampaign: searchParams.get('utm_campaign'),
                utmContent: searchParams.get('utm_content'),
                utmMedium: searchParams.get('utm_medium'),
                utmSource,
                utmCustomer: searchParams.get('utm_customer'),
                utmTerm: searchParams.get('utm_term'),
                utmOrigin: searchParams.get('utm_origin'),
                utmMbsy: searchParams.get('code'),
                utmInitiative: searchParams.get('utm_initiative'),
                liFatId: searchParams.get('li_fat_id'),
                gclid: searchParams.get('gclid'),
                fbclid: searchParams.get('fbclid'),
                irclickid: searchParams.get('irclickid'),
                ttclid: searchParams.get('ttclid'),
                msclkid: searchParams.get('msclkid'),
                promo: searchParams.get('promo'),
                referralSource: searchParams.get('referral_source'),
                referralPlatform: searchParams.get('referral_platform'),
                placement:
                    searchParams.get('placement') ||
                    searchParams.get('utm_placement'),
                adId: searchParams.get('ad_id'),
                sourceTemplateId: searchParams.get('templateId'),
                templateRedeemCode: searchParams.get('templateRedeemCode'),
                isGalleryTemplate: searchParams.get('isGalleryTemplate'),
                utm_buynowflow: searchParams.get('buynowflow'),
                partnership,
                full_url: urlValue
            });

            utms.other_utm_params = addRemainingUtmsFromQueryString(
                utms,
                searchParams
            );
        }
    } catch (error) {
        trackError({ error, message: 'Error while extract utm params' });
    }

    return utms;
};

const addRemainingUtmsFromQueryString = (
    utms: Partial<UtmParams>,
    searchParams: URLSearchParams
): Record<string, string> => {
    const additionalUtms: Record<string, string> = {};
    const searchParamsItems = searchParams.entries();
    const knownKeys = new Set(
        Object.keys(utms).map(key => camelCase(key).toLowerCase())
    );

    for (const [key, value] of searchParamsItems) {
        const camelKey = camelCase(key).toLowerCase();
        // Only add params that aren't part of our UtmParams type
        if (!knownKeys.has(camelKey)) {
            additionalUtms[key.toLowerCase()] = value;
        }
    }

    return additionalUtms;
};

type QueryParams = {
    full_name?: string;
    email?: string;
    legacy?: string;
};

export const getDataFromQueryParams = (key: keyof QueryParams) => {
    const signupDataFromQueryString = queryString.parse(
        window.location.search
    ) as QueryParams;
    return signupDataFromQueryString[key];
};

export const setSignupLandingPageFormReferrer = (
    data: IMarketingSignupData
) => {
    const signupLandingPage =
        data?.signupLandingPage || data?.signup_landing_page;

    if (!signupLandingPage) {
        const referrer = document.referrer;
        try {
            const refUrl = new URL(referrer);
            if (refUrl.hostname.endsWith('honeybook.com')) {
                let refer_signup_landing_page = refUrl.pathname;
                if (refer_signup_landing_page.startsWith('/')) {
                    refer_signup_landing_page =
                        refer_signup_landing_page.substring(1);
                }
                if (refer_signup_landing_page) {
                    data.signup_landing_page = refer_signup_landing_page;
                }
            }
        } catch (e) {
            trackError({
                error: e,
                message:
                    'Failed to parse referrer URL during signup form submission, Parsing referrer URL for landing page'
            });
        }
    }
};

export const getDataFromMarketingSite = (): IMarketingSignupData => {
    const signupDataFromLocalStorage = compactObject(
        JSON.parse(window.localStorage.getItem(SIGNUP_DATA_STORAGE_KEY) || '{}')
    );
    const signupDataFromQueryString =
        compactObject(queryString.parse(window.location.search)) || {};
    if (!isEmpty(signupDataFromLocalStorage)) {
        if (
            signupDataFromQueryString.redirect === 'true' &&
            !!signupDataFromQueryString.couponCode
        ) {
            signupDataFromLocalStorage.couponCode =
                signupDataFromQueryString.couponCode;
        }
        setSignupLandingPageFormReferrer(signupDataFromLocalStorage);
        return signupDataFromLocalStorage;
    } else if (!isEmpty(signupDataFromQueryString)) {
        return signupDataFromQueryString;
    }
    return {};
};

export const getBuyNowExperienceData = () => {
    const marketingSiteData = getDataFromMarketingSite();
    const pricingBuyNowData: BuyNowData =
        marketingSiteData.dataForIntakeFormTest?.buyNowData || {};

    const landingPage = marketingSiteData.signup_landing_page;
    const forceBuyNowExperience =
        getQueryParam(BUY_NOW_EXPERIENCE_QUERY_PARAM) === 'true';

    // Navigate from Pricing page (Clicked on BUY NOW)
    const isPricingBuyNowExperience =
        forceBuyNowExperience ||
        (['pricing', 'lp/pricing', 'teams', 'p/for-teams'].includes(
            landingPage
        ) &&
            !!pricingBuyNowData.period &&
            !!pricingBuyNowData.source);

    const isReferralLandingPage = landingPage === 'invites';

    const isBuyNowExperience =
        isPricingBuyNowExperience || isReferralLandingPage;

    const isEmbeddedBuyNowExperience = isReferralLandingPage;

    return {
        isBuyNowExperience,
        isPricingBuyNowExperience,
        isReferralLandingPage,
        pricingBuyNowData,
        forceBuyNowExperience,
        isEmbeddedBuyNowExperience
    };
};
const compactObject = obj => {
    return Object.fromEntries(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(obj).filter(([key, value]) => value != null)
    );
};
export const signupDataFromMarketing = getDataFromMarketingSite();

export const utmParams = getUtmParams();

export const getUserData = (formData: ISignupUserData = {}): UserData => {
    const _utmParams = getUtmParams(formData);
    return {
        ...signupDataFromMarketing,
        ...compactObject(formData),
        ..._utmParams,
        google_sso_variant: 'regular',
        hb_anonymous_id: getCookie(HB_ANONYMOUS_ID_COOKIE_NAME)
    };
};

export const getForterToken = (): string | undefined => {
    try {
        let fToken: string | undefined = (window as any).fToken;
        if (!fToken) {
            const value = '; ' + document.cookie;
            const parts = value.split('; forterToken=');
            if (parts?.length > 1) {
                fToken = parts.pop().split(';').shift();
            }
        }
        return fToken;
    } catch (err) {
        console.error(err);
        return undefined;
    }
};

export const removeLocalStorageItem = (key: string): void => {
    window.localStorage.removeItem(key);
};

export const getParsedSessionStorageItem = <T>({
    key,
    logger
}: {
    key: string;
    logger: LoggerService;
}): T | null => {
    try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (error) {
        logger.trackError({
            error,
            message: 'Failed to parse session storage item'
        });
        return null;
    }
};

/**
 * Prefills the website URL field based on the email domain
 * @param email - The email address to extract domain from
 * @param setFieldValue - Formik's setFieldValue function
 * @param isShowWebsiteQuestion - Whether to show the website question
 * @param fieldKey - The key of the field to prefill (default is 'websiteUrl')
 */
export const prefillWebsiteUrl = (
    email: string,
    setFieldValue: (field: string, value: any) => void,
    isShowWebsiteQuestion: boolean,
    fieldKey: string = 'websiteUrl'
): void => {
    if (isShowWebsiteQuestion) {
        if (isEmailValid(email) && isUniqueEmailDomain(email)) {
            setFieldValue(fieldKey, `www.${getEmailDomain(email)}`);
        } else {
            setFieldValue(fieldKey, '');
        }
    }
};

/**
 * Creates a form submission handler that executes prefill logic and submit callbacks
 * @param isShowWebsiteQuestion - Whether to show website question
 * @param prefillWebsiteUrlFn - Function to prefill website URL
 * @param executeSubmitCallbacks - Function to execute submit callbacks
 * @returns Form submission handler function
 */
export const createFormSubmissionHandler = (
    isShowWebsiteQuestion: boolean,
    prefillWebsiteUrlFn: () => void,
    executeSubmitCallbacks: () => void
) => {
    return () => {
        if (isShowWebsiteQuestion) {
            prefillWebsiteUrlFn();
        }
        executeSubmitCallbacks();
    };
};
